<template>
  <div class="flex-child">
    <div class="body-container" style="padding-top: 30px;">
      <header>
        <div class="header-left">
          <button @click="goBack" class="back-button">
            <i class="fas fa-arrow-left"></i> Back
          </button>
          <h2>Article Management</h2>
        </div>
        <div class="header-actions">
          <div class="genesys-status">
            <span class="status-indicator connected">
              🟢 Article Management Ready
            </span>
          </div>
        </div>
      </header>

      <div id="subheader" v-if="processedData" class="member-info subheader-aligned">
        <div class="info-item">
          <span class="info-label">Source Type:</span>
          <span class="info-value">{{ getSourceTypeLabel() }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Output Format:</span>
          <span class="info-value">Genesys KB JSON</span>
        </div>
        <div class="info-item" v-if="processedData?.json?.documents">
          <span class="info-label">Total Articles:</span>
          <span class="info-value">{{ processedData.json.documents.length }}</span>
        </div>
        <div class="info-item" v-if="approvedArticles.length > 0">
          <span class="info-label">Approved:</span>
          <span class="info-value approved-count">{{ approvedArticles.length }}</span>
        </div>
        <div class="info-item" v-if="selectedKnowledgeBase">
          <span class="info-label">Target KB:</span>
          <span class="info-value">{{ selectedKnowledgeBase.name }}</span>
        </div>
        <div class="info-item" v-if="processedData?.import_result">
          <span class="info-label">Status:</span>
          <span class="info-value import-success">Imported Successfully</span>
        </div>
      </div>

      <!-- Generated Articles Section -->
      <div id="centerdiv" v-if="processedData?.json?.documents?.length">
        <div class="articles-section-wrapper">
          <div class="articles-section">
            <div class="articles-header">
              <h4>Generated Articles ({{ processedData.json.documents.length }})</h4>
              <div class="articles-header-actions">
                <div class="approval-summary-mini">
                  <span class="mini-stat approved">{{ approvedArticles.length }}</span>
                  <span class="mini-stat pending">{{ pendingArticles.length }}</span>
                  <span class="mini-stat rejected">{{ rejectedArticles.length }}</span>
                </div>
              </div>
            </div>
            
            <!-- Selection Info Bar -->
            <div class="selection-info-bar">
              <div class="selection-info">
                <span v-if="selectedArticles.length > 0">
                  {{ selectedArticles.length }} of {{ processedData.json.documents.length }} articles selected
                </span>
                <span v-else>
                  Select articles to manage
                </span>
              </div>
              
              <!-- Bulk Actions -->
              <div v-if="selectedArticles.length > 0 || approvedArticles.length > 0" class="bulk-actions">
                <!-- Import Status Message (left of import button) -->
                <div v-if="processedData.import_result" class="import-status-message success">
                  <i class="fas fa-check-circle"></i>
                  Successfully imported {{ importedApprovedCount || approvedArticles.length || 0 }} articles to {{ selectedKnowledgeBase?.name }}
                </div>
                <div v-else-if="processedData.import_error" class="import-status-message error">
                  <i class="fas fa-exclamation-circle"></i>
                  Import failed: {{ processedData.import_error }}
                </div>

                <!-- Import Button (left of Approve) -->
                <select
                  v-if="approvedArticles.length > 0 && !processedData.import_result && !selectedKnowledgeBase"
                  v-model="selectedKnowledgeBase"
                  class="kb-select-inline"
                  :disabled="loadingKnowledgeBases">
                  <option :value="null">{{ loadingKnowledgeBases ? 'Loading KBs...' : 'Select Knowledge Base' }}</option>
                  <option
                    v-for="kb in knowledgeBases"
                    :key="kb.id"
                    :value="kb">
                    {{ kb.name }}
                  </option>
                </select>
                <button
                  v-if="approvedArticles.length > 0 && !processedData.import_result"
                  @click="importApprovedArticles"
                  :disabled="importingManually || !selectedKnowledgeBase"
                  class="bulk-action-btn import-btn">
                  <i class="fas fa-upload"></i>
                  {{ importingManually ? 'Importing...' : `Import ${approvedArticles.length} Approved Article${approvedArticles.length !== 1 ? 's' : ''} to ${selectedKnowledgeBase ? selectedKnowledgeBase.name : 'Knowledge Base'}` }}
                </button>

                <!-- Selection Actions -->
                <button
                  v-if="selectedArticles.length > 0"
                  @click="approveSelectedArticles"
                  :disabled="processedData.import_result"
                  class="bulk-action-btn approve-btn">
                  <i class="fas fa-check"></i>
                  Approve Selected ({{ selectedArticles.length }})
                </button>
                <button
                  v-if="selectedArticles.length > 0"
                  @click="rejectSelectedArticles"
                  :disabled="processedData.import_result"
                  class="bulk-action-btn reject-btn">
                  <i class="fas fa-times"></i>
                  Reject Selected ({{ selectedArticles.length }})
                </button>
                <button
                  v-if="selectedArticles.length > 0"
                  @click="resetSelectedArticles"
                  :disabled="processedData.import_result"
                  class="bulk-action-btn reset-btn">
                  <i class="fas fa-undo"></i>
                  Reset Selected ({{ selectedArticles.length }})
                </button>
              </div>
            </div>
            
            <!-- Articles Table -->
            <div class="articles-table-container">
              <table class="articles-table">
                <thead>
                  <tr>
                    <th class="checkbox-column">
                      <div class="checkbox-header-wrapper">
                        <input
                          type="checkbox"
                          :checked="allArticlesSelected"
                          @change="toggleAllArticles"
                          :disabled="processedData.import_result"
                          class="select-all-checkbox"
                          title="Select/Deselect All">
                      </div>
                    </th>
                    <th class="title-column">Title</th>
                    <th class="category-column">Category</th>
                    <th class="status-column">Status</th>
                    <th class="visible-column">Visible</th>
                    <th class="labels-column">Labels</th>
                    <th class="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(article, index) in processedData.json.documents" 
                    :key="article.id || index"
                    class="article-row"
                    :class="getArticleRowClass(index)">
                    
                    <td class="checkbox-cell">
                      <input
                        type="checkbox"
                        :checked="selectedArticles.includes(index)"
                        @change="toggleArticleSelection(index)"
                        :disabled="processedData.import_result"
                        class="article-checkbox">
                    </td>

                    
                    <td class="title-cell">
                      <div class="article-title">{{ getCurrentArticleData(article).title }}</div>
                      <div class="article-preview">{{ getArticlePreview(article) }}</div>
                    </td>
                    <td class="category-cell">
                      <span class="category-badge rectangle-badge">{{ getCurrentArticleData(article).category.name }}</span>
                    </td>
                    <td class="status-cell">
                      <span class="status-badge rectangle-badge" :class="getArticleStatusClass(article)">
                        {{ getArticleStatus(article) }}
                      </span>
                    </td>
                    <td class="visible-cell">
                      <div class="visibility-toggle">
                        <input
                          type="checkbox"
                          :checked="getCurrentArticleData(article).visible"
                          @change="toggleArticleVisibility(index, $event.target.checked)"
                          :disabled="processedData.import_result"
                          class="visibility-checkbox">
                        <span class="visibility-label">{{ getCurrentArticleData(article).visible ? 'Visible' : 'Hidden' }}</span>
                      </div>
                    </td>
                    <td class="labels-cell">
                      <div class="labels-list">
                        <span 
                          v-for="label in getCurrentArticleData(article).labels" 
                          :key="label.name"
                          class="label-tag rectangle-badge">
                          {{ label.name }}
                        </span>
                      </div>
                    </td>
                    <td class="actions-cell">
                      <div class="action-buttons">
                        <button
                          @click="editArticle(index)"
                          :disabled="processedData.import_result"
                          class="action-btn edit-btn"
                          :title="'Edit Article: ' + getCurrentArticleData(article).title">
                          <i class="fas fa-edit"></i>
                          <span class="btn-text">Edit</span>
                        </button>
                        <button
                          @click="deleteArticle(index)"
                          :disabled="processedData.import_result"
                          class="action-btn delete-btn"
                          title="Delete Article">
                          <i class="fas fa-trash"></i>
                          <span class="btn-text">Remove</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Historical Articles Section -->
            <div v-if="processedData?.source_info" class="historical-articles-section">
              <div class="section-header">
                <h4>
                  <i class="fas fa-history"></i>
                  Historical Articles
                </h4>
                <button
                  @click="loadHistoricalArticles"
                  :disabled="loadingHistoricalArticles"
                  class="refresh-btn">
                  <i class="fas fa-refresh" :class="{ 'fa-spin': loadingHistoricalArticles }"></i>
                  Refresh
                </button>
              </div>

              <div class="historical-articles-subtitle">
                Articles from: {{ processedData.source_info }}
              </div>

              <!-- Loading State -->
              <div v-if="loadingHistoricalArticles" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading historical articles...</p>
              </div>

              <!-- Historical Articles Accordion (grouped by day) -->
              <div v-else-if="historicalArticles.length > 0" class="historical-accordion-app">
                <v-expansion-panels
                  v-model="openDayPanels"
                  multiple
                  variant="accordion"
                  class="historical-accordion">
                  <v-expansion-panel
                    v-for="(group, idx) in visibleHistoricalDays"
                    :key="group.label"
                    :value="idx">
                    <v-expansion-panel-title class="day-panel-title">
                      <div class="day-panel-header">
                        <i class="fas fa-calendar-day day-icon"></i>
                        <span class="day-label">{{ group.label }}</span>
                        <span class="day-count">{{ group.articles.length }} article{{ group.articles.length !== 1 ? 's' : '' }}</span>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="day-panel-content">
                      <table class="historical-articles-table">
                        <thead>
                          <tr>
                            <th class="col-title">Title</th>
                            <th class="col-time">Time</th>
                            <th class="col-kb">Knowledge Base</th>
                            <th class="col-status">Status</th>
                            <th class="col-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="article in getPaginatedArticles(group)" :key="article.id" class="article-row">
                            <td class="col-title">
                              <div class="article-title">{{ article.title }}</div>
                            </td>
                            <td class="col-time">
                              {{ article.created_at ? new Date(article.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : 'N/A' }}
                            </td>
                            <td class="col-kb">
                              <span class="kb-badge">{{ getKnowledgeBaseName(article.genesys_kb_id) }}</span>
                            </td>
                            <td class="col-status">
                              <span class="status-badge" :class="article.status.toLowerCase()">
                                {{ article.status }}
                              </span>
                            </td>
                            <td class="col-actions">
                              <div class="article-actions">
                                <button class="action-btn-small view-btn" @click="viewHistoricalArticle(article)" title="View">
                                  <i class="fas fa-eye"></i>
                                  <span>View</span>
                                </button>
                                <button class="action-btn-small delete-btn" @click="deleteHistoricalArticle(article)" title="Delete">
                                  <i class="fas fa-trash"></i>
                                  <span>Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- Per-panel pagination -->
                      <div v-if="getDayTotalPages(group) > 1" class="day-pagination">
                        <div class="day-pagination-info">
                          Showing {{ (getDayPage(group.label) - 1) * 25 + 1 }}–{{ Math.min(getDayPage(group.label) * 25, group.articles.length) }} of {{ group.articles.length }}
                        </div>
                        <div class="day-pagination-buttons">
                          <button
                            class="day-page-btn"
                            :disabled="getDayPage(group.label) === 1"
                            @click="setDayPage(group.label, getDayPage(group.label) - 1)">
                            <i class="fas fa-angle-left"></i>
                          </button>
                          <button
                            v-for="p in getDayTotalPages(group)"
                            :key="p"
                            class="day-page-btn"
                            :class="{ active: p === getDayPage(group.label) }"
                            @click="setDayPage(group.label, p)">
                            {{ p }}
                          </button>
                          <button
                            class="day-page-btn"
                            :disabled="getDayPage(group.label) === getDayTotalPages(group)"
                            @click="setDayPage(group.label, getDayPage(group.label) + 1)">
                            <i class="fas fa-angle-right"></i>
                          </button>
                        </div>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>

                <!-- Load more days -->
                <div v-if="hasMoreDays" class="load-more-days">
                  <button class="load-more-btn" @click="loadMoreDays">
                    <i class="fas fa-calendar-plus"></i>
                    Load {{ Math.min(30, remainingDayCount) }} More Day{{ Math.min(30, remainingDayCount) !== 1 ? 's' : '' }}
                    <span class="remaining-count">({{ remainingDayCount }} remaining)</span>
                  </button>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="historical-empty-state">
                <div class="empty-icon">
                  <i class="fas fa-file-alt"></i>
                </div>
                <p>No historical articles found for this source.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!processedData?.json?.documents?.length" class="empty-state">
        <i class="fas fa-file-alt"></i>
        <h3>No Articles Available</h3>
        <p>Generate articles from the Knowledge Inventory to manage them here.</p>
        <button @click="$router.push('/articles_generator')" class="primary-btn">
          <i class="fas fa-plus"></i>
          Generate Articles
        </button>
      </div>
    </div>

    <!-- Historical Article Preview Modal -->
    <div
      v-if="showHistoricalArticleModal"
      class="article-preview-modal-overlay"
      @click.self="showHistoricalArticleModal = false">
      <div class="article-preview-modal">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="header-icon">📄</div>
            <div class="header-text">
              <h3>{{ previewingHistoricalArticle?.title || 'Article Preview' }}</h3>
              <div class="header-meta" v-if="previewingHistoricalArticle">
                <span class="status-badge" :class="previewingHistoricalArticle.status?.toLowerCase()">
                  {{ previewingHistoricalArticle.status }}
                </span>
                <span class="date-badge">{{ formatHistoricalDate(previewingHistoricalArticle.created_at) }}</span>
              </div>
            </div>
          </div>
          <button type="button" class="close" @click="showHistoricalArticleModal = false">
            ✕
          </button>
        </div>

        <!-- Content -->
        <div class="article-preview-content" v-if="previewingHistoricalArticle">
          <!-- Article Info Grid -->
          <div class="article-info-grid">
            <div class="article-info-item">
              <strong>Source URL</strong>
              <span class="source-url">{{ previewingHistoricalArticle.source_url || previewingHistoricalArticle.sourceUrl || 'Not specified' }}</span>
            </div>
            <div class="article-info-item">
              <strong>KB ID</strong>
              <span class="kb-id">{{ previewingHistoricalArticle.genesys_kb_id }}</span>
            </div>
            <div class="article-info-item" v-if="previewingHistoricalArticle.genesys_document_id">
              <strong>Genesys Document ID</strong>
              <span class="genesys-document-id">{{ previewingHistoricalArticle.genesys_document_id }}</span>
            </div>
            <div class="article-info-item" v-if="previewingHistoricalArticle.genesys_document_state">
              <strong>Document State</strong>
              <span class="genesys-document-state">{{ previewingHistoricalArticle.genesys_document_state }}</span>
            </div>
            <div class="article-info-item" v-if="previewingHistoricalArticle.import_job_id">
              <strong>Import Job ID</strong>
              <span class="import-job-id">{{ previewingHistoricalArticle.import_job_id }}</span>
            </div>
            <div class="article-info-item" v-if="previewingHistoricalArticle.import_date_created">
              <strong>Import Date</strong>
              <span class="import-date">{{ new Date(previewingHistoricalArticle.import_date_created).toLocaleString() }}</span>
            </div>
          </div>

          <!-- Meta Information -->
          <div class="article-meta-info" v-if="previewingHistoricalArticle.data">
            <div class="article-meta-row" v-if="getContentField(previewingHistoricalArticle.data, 'category.name')">
              <strong>Category</strong>
              <span class="category-tag">{{ getContentField(previewingHistoricalArticle.data, 'category.name') }}</span>
            </div>

            <div class="article-meta-row" v-if="getContentField(previewingHistoricalArticle.data, 'labels')?.length">
              <strong>Labels</strong>
              <div class="tags-container">
                <span
                  v-for="label in getContentField(previewingHistoricalArticle.data, 'labels')"
                  :key="label.name"
                  class="label-tag">
                  {{ label.name }}
                </span>
              </div>
            </div>

            <div class="article-meta-row" v-if="getContentField(previewingHistoricalArticle.data, 'alternatives')?.length">
              <strong>Alternatives</strong>
              <div class="tags-container">
                <span
                  v-for="alt in getContentField(previewingHistoricalArticle.data, 'alternatives')"
                  :key="alt.phrase"
                  class="alternative-item">
                  {{ alt.phrase }}
                </span>
              </div>
            </div>
          </div>

          <!-- Article Content -->
          <div class="content-preview-section">
            <h4>Content</h4>
            <div class="content-text" v-html="renderArticleContent(previewingHistoricalArticle.data)"></div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button @click="showHistoricalArticleModal = false" class="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Article Editor Modal -->
    <div v-if="showArticleEditor" class="editor-modal-overlay">
      <div class="editor-modal" style="max-width: 90%; width: 1200px;">
        <div class="editor-modal-header">
          <h3>Edit Article</h3>
          <button @click="closeArticleEditor" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="editor-modal-body">
          <div class="editor-left-panel">
            <!-- Article Title -->
            <div class="edit-field">
              <label>Article Title</label>
              <input
                v-model="editingArticle.draft.title"
                class="form-control"
                placeholder="Enter article title..."
                style="font-size: 16px; margin-top: 3px;" />
            </div>

            <!-- Category Selection -->
            <div class="edit-field">
              <label>Category</label>
              <select 
                v-model="editingArticle.draft.category.name"
                class="form-control">
                <option>General Information</option>
                <option>Account and Billing</option>
                <option>Technical Support</option>
                <option>Product Features</option>
                <option>Troubleshooting</option>
              </select>
            </div>

            <!-- Status Selection -->
            <div class="edit-field">
              <label>Status</label>
              <select 
                v-model="editingArticleStatus"
                @change="updateArticleStatus"
                class="form-control">
                <option>Draft</option>
                <option>Published</option>
              </select>
            </div>

            <!-- Visibility Toggle -->
            <div class="edit-field">
              <label>
                <input 
                  type="checkbox" 
                  v-model="editingArticle.draft.visible"
                  style="margin-right: 8px;">
                Article Visible
              </label>
            </div>

            <!-- Labels Input -->
            <div class="edit-field">
              <label>Labels (comma-separated)</label>
              <input
                v-model="labelsText"
                class="form-control"
                placeholder="label1, label2, label3"
                @input="updateLabels" />
            </div>

            <!-- Current Labels Display -->
            <div class="edit-field">
              <label>Current Labels</label>
              <div class="current-labels-display">
                <span 
                  v-for="label in editingArticle.draft.labels" 
                  :key="label.name"
                  class="current-label-tag">
                  {{ label.name }}
                </span>
                <span v-if="!editingArticle.draft.labels?.length" class="no-labels">
                  No labels assigned
                </span>
              </div>
            </div>

            <!-- Alternatives Section -->
            <div class="edit-field">
              <label>Question Alternatives</label>
              <div class="alternatives-section">
                <div v-for="(alternative, index) in editingArticle.draft.alternatives" :key="index" class="alternative-item">
                  <input
                    v-model="alternative.phrase"
                    class="form-control alternative-input"
                    placeholder="Alternative question phrasing..." />
                  <button 
                    @click="removeAlternative(index)"
                    class="btn-danger-small btn-rounded">
                    Remove
                  </button>
                </div>
                <button 
                  @click="addAlternative"
                  class="btn-secondary btn-rounded">
                  Add Alternative
                </button>
              </div>
            </div>
          </div>
          
          <div class="editor-right-panel">

            <!-- Variation Tabs — above the content box -->
            <div v-if="editingArticleVariations.length > 0" class="variation-tabs-bar">
              <button
                v-for="(variation, idx) in editingArticleVariations"
                :key="variation.name"
                class="variation-tab"
                :class="{ 'variation-tab-active': activeVariationIndex === idx }"
                @click="switchVariation(idx)">
                <span class="variation-tab-name">{{ variation.name }}</span>
                <div class="variation-tab-chips" v-if="getVariationTouchpoints(variation).length">
                  <span
                    v-for="tp in getVariationTouchpoints(variation)"
                    :key="tp"
                    class="variation-tab-chip"
                    :class="getTouchpointChipColor(tp)">
                    {{ tp }}
                  </span>
                </div>
              </button>
            </div>

            <!-- Touchpoint sub-tabs for the active variation -->
            <div v-if="editingArticleVariations.length > 0" class="tp-subtabs-bar">
              <span
                v-for="tp in getVariationTouchpoints(editingArticleVariations[activeVariationIndex])"
                :key="tp"
                class="tp-subtab">
                <span class="tp-subtab-dot" :style="{ background: getTouchpointDotColor(tp) }"></span>
                {{ tp }}
              </span>
            </div>

            <div class="content-editor" :class="{ 'content-editor-tabbed': editingArticleVariations.length > 0 }">
              <div class="content-editor-header">
                <h4>Article Content</h4>
              </div>

              <!-- Content Blocks Editor -->
              <div class="wysiwyg-editor-container">
                <QuillEditor
                  ref="quillEditor"
                  v-model:content="wysiwygContent"
                  :options="quillOptions"
                  content-type="html"
                  class="wysiwyg-editor"
                  @ready="onEditorReady"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div class="editor-modal-footer">
          <button
            @click="saveArticleChanges"
            class="btn-primary btn-rounded">
            Save Changes
          </button>
          <button
            @click="closeArticleEditor"
            class="btn-secondary btn-rounded">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div v-if="showDeleteModal" class="modal-overlay" @click.self="cancelDelete">
    <div class="delete-confirmation-modal">
      <div class="modal-header-delete">
        <div class="warning-icon">⚠️</div>
        <h3>Delete Article</h3>
      </div>
      <div class="modal-body-delete">
        <p>Are you sure you want to delete this article?</p>
        <p class="article-title-delete">"{{ articleToDelete?.title }}"</p>
        <p class="warning-text">This action cannot be undone. The article will be removed from both the database and Genesys Knowledge Base.</p>
      </div>
      <div class="modal-footer-delete">
        <button @click="cancelDelete" class="btn-cancel" :disabled="isDeleting">Cancel</button>
        <button @click="confirmDelete" class="btn-delete" :disabled="isDeleting">{{ isDeleting ? 'Deleting...' : 'Delete' }}</button>
      </div>
    </div>
  </div>

  <!-- Toast Notification -->
  <transition name="toast">
    <div v-if="showToast" :class="['toast-notification', toastType]">
      <div class="toast-icon">
        <i v-if="toastType === 'success'" class="fas fa-check-circle"></i>
        <i v-else-if="toastType === 'error'" class="fas fa-exclamation-circle"></i>
        <i v-else class="fas fa-info-circle"></i>
      </div>
      <div class="toast-message">{{ toastMessage }}</div>
      <button @click="showToast = false" class="toast-close">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </transition>
</template>

<script setup>
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'

// Router
const router = useRouter()

// Reactive data
const processedData = ref(null)
const selectedKnowledgeBase = ref(null)
const knowledgeBases = ref([])
const loadingKnowledgeBases = ref(false)
const importingManually = ref(false)
const importedApprovedCount = ref(0)

// Article management state
const selectedArticles = ref([])
const articleApprovalStates = ref({}) // { articleIndex: 'approved' | 'rejected' | 'pending' }

// Article editor state
const showArticleEditor = ref(false)
const editingArticle = ref(null)
const editingArticleIndex = ref(-1)
const editingArticleStatus = ref('Draft')
const labelsText = ref('')
const activeVariationIndex = ref(0)

const editingArticleVariations = computed(() => {
  if (!editingArticle.value) return []
  const articleData = editingArticle.value.published || editingArticle.value.draft || {}
  return articleData.variations || []
})

// WYSIWYG Editor state
const wysiwygContent = ref('')
const editorInstance = ref(null)
const quillEditor = ref(null)

// Historical articles state
const historicalArticles = ref([])
const loadingHistoricalArticles = ref(false)
const openDayPanels = ref([])
const visibleDayCount = ref(30)
const dayPanelPages = ref({}) // { [dayLabel]: currentPage }
const showHistoricalArticleModal = ref(false)
const previewingHistoricalArticle = ref(null)
const pendingContent = ref('')

// Delete confirmation modal state
const showDeleteModal = ref(false)
const articleToDelete = ref(null)
const isDeleting = ref(false)

// Toast notification state
const toastMessage = ref('')
const toastType = ref('success')
const showToast = ref(false)

// Quill Editor Configuration
const quillOptions = ref({
  theme: 'snow',
  modules: {
    toolbar: {
      container: [
        [{ 'header': [3, 4, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  },
  formats: ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link', 'image'],
  placeholder: 'Write your article content here...'
})

// Image upload handler for Quill
function imageHandler() {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', 'image/*')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]
    if (file) {
      try {
        // Show uploading state
        const company = localStorage.getItem("companyID") || "default"
        const formData = new FormData()
        formData.append('file', file)
        formData.append('company', company)

        const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/upload-image`, {
          method: 'POST',
          body: formData
        })

        if (response.ok) {
          const result = await response.json()
          const quill = quillEditor.value.getQuill()
          const range = quill.getSelection(true)
          quill.insertEmbed(range.index, 'image', result.url)
          quill.setSelection(range.index + 1)
        } else {
          console.error('Image upload failed')
          alert('Failed to upload image. Please try again.')
        }
      } catch (error) {
        console.error('Error uploading image:', error)
        alert('Error uploading image. Please try again.')
      }
    }
  }
}

// Helper functions
const getSourceTypeLabel = () => {
  return 'Article Management'
}

const goBack = () => {
  router.push('/articles_generator')
}

const getCurrentArticleData = (article) => {
  const data = article.published || article.draft || article
  return {
    title: data.title || 'Untitled',
    category: data.category || { name: 'Uncategorized' },
    visible: data.visible !== undefined ? data.visible : true,
    labels: data.labels || []
  }
}

// Link articles with Genesys documents based on import date
const linkArticlesWithGenesysDocuments = async () => {
  if (!processedData.value?.json?.documents || !processedData.value.import_result) {
    console.log('No import result found for linking')
    return
  }

  try {
    const company = localStorage.getItem("companyID") || "default"
    const importResult = processedData.value.import_result
    
    // Check if we have the required import information
    if (!importResult.dateCreated || !importResult.knowledgeBase?.id) {
      console.warn('Import result missing required data for linking')
      return
    }

    const importDate = importResult.dateCreated
    const knowledgeBaseId = importResult.knowledgeBase.id
    
    try {
      // Fetch documents created around the same time as the import
      const docsResponse = await fetch(
        `${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases/${knowledgeBaseId}/documents?company=${company}&created_after=${importDate}`
      )
      
      if (docsResponse.ok) {
        const docsData = await docsResponse.json()
        const genesysDocuments = docsData.documents || []
        
        console.log(`Found ${genesysDocuments.length} Genesys documents to link with`)
        
        // Link each approved article with a matching Genesys document
        for (let i = 0; i < processedData.value.json.documents.length; i++) {
          const article = processedData.value.json.documents[i]
          const approvalStatus = articleApprovalStates.value[i]
          
          // Only link approved articles that were likely imported
          if (approvalStatus === 'approved') {
            const articleTitle = getCurrentArticleData(article).title
            
            const matchingDoc = genesysDocuments.find(doc => {
              // Check if the document was created within 5 minutes of the import
              const docCreated = new Date(doc.dateCreated)
              const importCreated = new Date(importDate)
              const timeDiff = Math.abs(docCreated - importCreated)
              const fiveMinutes = 5 * 60 * 1000
              
              // Also check title similarity
              const titleMatch = doc.title && articleTitle && 
                (doc.title.toLowerCase() === articleTitle.toLowerCase() || 
                 doc.title.toLowerCase().includes(articleTitle.toLowerCase()) ||
                 articleTitle.toLowerCase().includes(doc.title.toLowerCase()))
              
              return timeDiff <= fiveMinutes && titleMatch
            })
            
            if (matchingDoc) {
              // Add Genesys document info to the article
              article.genesys_document = {
                id: matchingDoc.id,
                title: matchingDoc.title,
                state: matchingDoc.state,
                dateCreated: matchingDoc.dateCreated,
                selfUri: matchingDoc.selfUri
              }
              console.log(`Linked article "${articleTitle}" with Genesys doc ${matchingDoc.id}`)
            }
          }
        }
      } else {
        console.warn(`Failed to fetch documents from KB ${knowledgeBaseId}`)
      }
    } catch (error) {
      console.warn(`Failed to fetch documents for KB ${knowledgeBaseId}:`, error)
    }
  } catch (error) {
    console.warn('Failed to link articles with Genesys documents:', error)
  }
}

const getArticlePreview = (article) => {
  const articleData = article.published || article.draft || {}
  const blocks = articleData.variations?.[0]?.body?.blocks || articleData.blocks || []
  
  const text = blocks
    .map(block => {
      if (block.type === 'Paragraph' && block.paragraph?.blocks) {
        return block.paragraph.blocks
          .filter(textBlock => textBlock.type === 'Text')
          .map(textBlock => textBlock.text?.text || '')
          .join('')
      } else if (block.type === 'UnorderedList' || block.type === 'OrderedList') {
        const listItems = block.list?.blocks || []
        return listItems
          .filter(item => item.type === 'ListItem')
          .map(item => {
            const itemBlocks = item.listItem?.blocks || item.blocks || []
            return itemBlocks
              .filter(textBlock => textBlock.type === 'Text')
              .map(textBlock => textBlock.text?.text || '')
              .join('')
          })
          .join(', ')
      }
      return ''
    })
    .join(' ')
    .trim()
  
  return text ? (text.length > 150 ? text.substring(0, 150) + '...' : text) : 'No content available'
}

// Content conversion functions
const convertGenesysToHtml = (article, variationIndex = 0) => {
  try {
    const articleData = article.published || article.draft || {}
    const blocks = articleData.variations?.[variationIndex]?.body?.blocks || []
    
    let html = ''
    for (const block of blocks) {
      if (block.type === 'Paragraph') {
        const fontType = block.paragraph?.properties?.fontType || 'Paragraph'
        let tag = fontType === 'Heading3' ? 'h3' : fontType === 'Heading4' ? 'h4' : 'p'
        
        let content = ''
        const textBlocks = block.paragraph?.blocks || []
        
        for (const textBlock of textBlocks) {
          if (textBlock.type === 'Text') {
            let text = textBlock.text?.text || ''

            if (textBlock.text?.marks?.includes('Bold')) {
              text = `<strong>${text}</strong>`
            }
            if (textBlock.text?.hyperlink) {
              text = `<a href="${textBlock.text.hyperlink}">${text}</a>`
            }

            content += text
          }
          else if (textBlock.type === 'Image') {
            // Handle Image blocks within paragraphs
            const imageUrl = textBlock.image?.url || ''
            const altText = textBlock.image?.properties?.altText || 'Image'
            if (imageUrl) {
              content += `<img src="${imageUrl}" alt="${altText}" style="max-width: 100%; height: auto;" />`
            }
          }
        }
        
        if (content) {
          html += `<${tag}>${content}</${tag}>`
        }
      }
      else if (block.type === 'OrderedList' || block.type === 'UnorderedList') {
        const listTag = block.type === 'OrderedList' ? 'ol' : 'ul'
        html += `<${listTag}>`
        
        const listItems = block.list?.blocks || []
        for (const item of listItems) {
          if (item.type === 'ListItem') {
            let itemContent = ''
            const itemBlocks = item.listItem?.blocks || item.blocks || []
            
            for (const itemBlock of itemBlocks) {
              if (itemBlock.type === 'Text') {
                let text = itemBlock.text?.text || ''
                if (itemBlock.text?.marks?.includes('Bold')) {
                  text = `<strong>${text}</strong>`
                }
                if (itemBlock.text?.hyperlink) {
                  text = `<a href="${itemBlock.text.hyperlink}">${text}</a>`
                }
                itemContent += text
              }
            }
            
            if (itemContent.trim()) {
              html += `<li>${itemContent}</li>`
            }
          }
        }
        
        html += `</${listTag}>`
      }
    }
    
    return html || '<p>Start editing your article content here...</p>'
  } catch (error) {
    console.error('Error converting Genesys to HTML:', error)
    return '<p>Error loading content - please refresh and try again.</p>'
  }
}

const convertHtmlToGenesys = (html) => {
  if (!html || html.trim() === '') {
    return []
  }
  
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  const blocks = []
  
  for (const element of tempDiv.children) {
    const tagName = element.tagName.toLowerCase()
    
    if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
      let fontType = 'Paragraph'
      if (tagName === 'h3') fontType = 'Heading3'
      else if (tagName === 'h4') fontType = 'Heading4'
      
      const textBlocks = []
      
      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          const rawText = node.textContent
          if (rawText.trim()) {
            textBlocks.push({
              type: 'Text',
              text: { text: rawText }
            })
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.tagName.toLowerCase() === 'img') {
            // Handle image elements
            textBlocks.push({
              type: 'Image',
              image: {
                url: node.src || '',
                properties: {
                  width: 56.25,
                  widthWithUnit: {
                    value: 900.0,
                    unit: 'Px'
                  },
                  altText: node.alt || 'Image'
                }
              }
            })
          } else {
            const trimmedText = node.textContent.trim()
            if (trimmedText) {
              const text = { text: trimmedText }

              if (['strong', 'b'].includes(node.tagName.toLowerCase())) {
                text.marks = ['Bold']
              }
              if (node.tagName.toLowerCase() === 'a' && node.href) {
                text.hyperlink = node.href
              }

              textBlocks.push({ type: 'Text', text: text })
            }
          }
        }
      }
      
      if (textBlocks.length > 0) {
        blocks.push({
          type: 'Paragraph',
          paragraph: {
            blocks: textBlocks,
            properties: { fontType }
          }
        })
      } else if (fontType === 'Paragraph') {
        // Empty <p> — TipTap strips   spacer content; preserve as a spacer block
        blocks.push({
          type: 'Paragraph',
          paragraph: {
            blocks: [{ type: 'Text', text: { text: ' ' } }],
            properties: { fontType: 'Paragraph' }
          }
        })
      }
    }
    else if (['ol', 'ul'].includes(tagName)) {
      const listType = tagName === 'ol' ? 'OrderedList' : 'UnorderedList'
      const listItems = []
      
      for (const li of element.children) {
        if (li.tagName.toLowerCase() === 'li') {
          const itemBlocks = []
          
          for (const node of li.childNodes) {
            if (node.nodeType === Node.TEXT_NODE) {
              const rawText = node.textContent
              if (rawText.trim()) {
                itemBlocks.push({
                  type: 'Text',
                  text: { text: rawText }
                })
              }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const trimmedText = node.textContent.trim()
              if (trimmedText) {
                const text = { text: trimmedText }

                if (['strong', 'b'].includes(node.tagName.toLowerCase())) {
                  text.marks = ['Bold']
                }
                if (node.tagName.toLowerCase() === 'a' && node.href) {
                  text.hyperlink = node.href
                }

                itemBlocks.push({ type: 'Text', text: text })
              }
            }
          }
          
          if (itemBlocks.length > 0) {
            listItems.push({
              type: 'ListItem',
              blocks: itemBlocks,
              properties: { fontType: 'Paragraph' }
            })
          }
        }
      }
      
      if (listItems.length > 0) {
        blocks.push({
          type: listType,
          list: { blocks: listItems }
        })
      }
    }
  }
  
  return blocks
}

const getArticleStatus = (article) => {
  return article.published ? 'Published' : 'Draft'
}

const getArticleStatusClass = (article) => {
  return article.published ? 'published' : 'draft'
}

// Approval system computed properties
const approvedArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    articleApprovalStates.value[index] === 'approved'
  )
})

const pendingArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    !articleApprovalStates.value[index] || articleApprovalStates.value[index] === 'pending'
  )
})

const rejectedArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    articleApprovalStates.value[index] === 'rejected'
  )
})

const allArticlesSelected = computed(() => {
  if (!processedData.value?.json?.documents?.length) return false
  return selectedArticles.value.length === processedData.value.json.documents.length
})

// Approval functions
const getApprovalStatus = (index) => {
  return articleApprovalStates.value[index] || 'pending'
}


const getArticleRowClass = (index) => {
  const status = getApprovalStatus(index)
  const isSelected = selectedArticles.value.includes(index)
  
  return {
    'article-approved': status === 'approved',
    'article-rejected': status === 'rejected',
    'article-pending': status === 'pending',
    'article-selected': isSelected
  }
}


const toggleArticleSelection = (index) => {
  const selectedIndex = selectedArticles.value.indexOf(index)
  if (selectedIndex > -1) {
    selectedArticles.value.splice(selectedIndex, 1)
  } else {
    selectedArticles.value.push(index)
  }
}

const toggleAllArticles = () => {
  if (allArticlesSelected.value) {
    selectedArticles.value = []
  } else {
    selectedArticles.value = processedData.value.json.documents.map((_, index) => index)
  }
}

const approveSelectedArticles = () => {
  if (selectedArticles.value.length === 0) return
  
  selectedArticles.value.forEach(index => {
    articleApprovalStates.value[index] = 'approved'
  })
  
  // Save to localStorage
  localStorage.setItem('articleApprovalStates', JSON.stringify(articleApprovalStates.value))
  
  console.log(`Approved ${selectedArticles.value.length} articles`)
}

const rejectSelectedArticles = () => {
  if (selectedArticles.value.length === 0) return
  
  selectedArticles.value.forEach(index => {
    articleApprovalStates.value[index] = 'rejected'
  })
  
  // Save to localStorage
  localStorage.setItem('articleApprovalStates', JSON.stringify(articleApprovalStates.value))
  
  console.log(`Rejected ${selectedArticles.value.length} articles`)
}

const resetSelectedArticles = () => {
  if (selectedArticles.value.length === 0) return
  
  selectedArticles.value.forEach(index => {
    articleApprovalStates.value[index] = 'pending'
  })
  
  // Save to localStorage
  localStorage.setItem('articleApprovalStates', JSON.stringify(articleApprovalStates.value))
  
  console.log(`Reset ${selectedArticles.value.length} articles to pending`)
}



const toggleArticleVisibility = (index, visible) => {
  const article = processedData.value.json.documents[index]
  if (article.published) {
    article.published.visible = visible
  }
  if (article.draft) {
    article.draft.visible = visible
  }
}

const editArticle = async (index) => {
  console.log('=== EDITING ARTICLE ===')
  console.log('Editing article at index:', index)
  
  editingArticleIndex.value = index
  const originalArticle = processedData.value.json.documents[index]
  
  if (!originalArticle) {
    console.error('Article not found at index:', index)
    return
  }

  // Create editing copy (preserve ID)
  editingArticle.value = JSON.parse(JSON.stringify(originalArticle))
  
  // Determine status
  editingArticleStatus.value = originalArticle.published ? 'Published' : 'Draft'
  
  // Ensure proper structure
  if (!editingArticle.value.draft) {
    editingArticle.value.draft = originalArticle.published || originalArticle || {
      title: 'Untitled',
      category: { name: 'General Information' },
      visible: true,
      labels: [],
      alternatives: [],
      blocks: []
    }
  }

  // Set up labels
  const articleData = originalArticle.published || originalArticle.draft
  if (articleData?.labels?.length > 0) {
    labelsText.value = articleData.labels.map(l => l.name).join(', ')
  } else {
    labelsText.value = ''
  }
  
  // Reset to first variation when opening editor
  activeVariationIndex.value = 0

  // Convert content to HTML
  const convertedHtml = convertGenesysToHtml(originalArticle, 0)
  console.log('Editing article:', {
    id: originalArticle.id,
    title: articleData?.title,
    status: editingArticleStatus.value,
    hasDraft: !!originalArticle.draft,
    hasPublished: !!originalArticle.published
  })
  
  showArticleEditor.value = true
  
  await nextTick()
  
  // Set content in editor
  setTimeout(() => {
    if (editorInstance.value) {
      console.log('Setting content on ready editor')
      editorInstance.value.root.innerHTML = convertedHtml
    } else {
      console.log('Editor not ready, storing content for later')
      pendingContent.value = convertedHtml
    }
    
    wysiwygContent.value = convertedHtml
  }, 150)
}

const TOUCHPOINT_NAMES = {
  'bec0bb26-6b11-4917-94ff-4d7106e44ec1': 'Support Center',
  'd49d6170-8e3b-47e2-9f1c-a030822c7f4f': 'Knowledge App',
  '6a5414b6-0b3b-4c33-bedb-a873c5d98ec0': 'Agent Assist',
  '4969356e-3432-482b-9d6f-969fed765d17': 'Bot Flows',
  '101e4cb4-db4d-44f8-b8cf-ee6c8359bb0b': 'Digital Bot Flow',
}

const getVariationTouchpoints = (variation) => {
  const values = variation.contexts?.[0]?.values || []
  return values.map(v => TOUCHPOINT_NAMES[v.id]).filter(Boolean)
}

const TOUCHPOINT_CHIP_COLORS = {
  'Agent Assist':     'tp-chip-blue',
  'Support Center':   'tp-chip-teal',
  'Knowledge App':    'tp-chip-teal',
  'Bot Flows':        'tp-chip-purple',
  'Digital Bot Flow': 'tp-chip-purple',
}
const TOUCHPOINT_DOT_COLORS = {
  'Agent Assist':     '#3b82f6',
  'Support Center':   '#10b981',
  'Knowledge App':    '#10b981',
  'Bot Flows':        '#8b5cf6',
  'Digital Bot Flow': '#8b5cf6',
}

const getTouchpointChipColor = (label) => TOUCHPOINT_CHIP_COLORS[label] || 'tp-chip-gray'
const getTouchpointDotColor  = (label) => TOUCHPOINT_DOT_COLORS[label]  || '#9ca3af'

const switchVariation = (idx) => {
  // Save pending changes to current variation BEFORE switching
  if (activeVariationIndex.value !== idx && wysiwygContent.value) {
    const blocks = convertHtmlToGenesys(wysiwygContent.value)
    const articleData = editingArticleStatus.value === 'Draft'
      ? editingArticle.value.draft
      : editingArticle.value.published

    if (articleData.variations && articleData.variations.length > activeVariationIndex.value) {
      articleData.variations[activeVariationIndex.value] = {
        ...articleData.variations[activeVariationIndex.value],
        body: { blocks }
      }
      console.log(`Saved pending changes to variation "${articleData.variations[activeVariationIndex.value].name}"`)
    }
  }

  // Now switch to new variation
  activeVariationIndex.value = idx
  if (!editingArticle.value) return
  const html = convertGenesysToHtml(editingArticle.value, idx)
  wysiwygContent.value = html
  if (editorInstance.value) {
    editorInstance.value.root.innerHTML = html
  }
  console.log(`Switched to variation "${editingArticle.value[editingArticleStatus.value === 'Draft' ? 'draft' : 'published']?.variations[idx]?.name}"`)
}

const onEditorReady = (quill) => {
  console.log('Editor is ready:', quill)
  editorInstance.value = quill
  
  // If we have pending content, set it now
  if (pendingContent.value) {
    console.log('Setting pending content:', pendingContent.value)
    quill.root.innerHTML = pendingContent.value
    pendingContent.value = ''
  }
}

const saveArticleChanges = () => {
  if (editingArticleIndex.value >= 0 && editingArticle.value) {
    console.log('=== SAVING ARTICLE ===')
    console.log('Editing article index:', editingArticleIndex.value)
    console.log('Active variation index:', activeVariationIndex.value)

    // Get the ORIGINAL article to preserve its ID and approval status
    const originalArticle = processedData.value.json.documents[editingArticleIndex.value]
    const originalId = originalArticle.id
    const currentApprovalStatus = articleApprovalStates.value[editingArticleIndex.value] || 'pending'

    console.log('Original article ID:', originalId)
    console.log('Current approval status BEFORE save:', currentApprovalStatus)

    // Convert WYSIWYG HTML back to Genesys blocks
    const blocks = convertHtmlToGenesys(wysiwygContent.value)

    // Update ONLY the selected variation, preserve all others
    const articleData = editingArticleStatus.value === 'Draft'
      ? editingArticle.value.draft
      : editingArticle.value.published

    if (articleData.variations && articleData.variations.length > 0) {
      // Update the specific variation at activeVariationIndex
      articleData.variations[activeVariationIndex.value] = {
        ...articleData.variations[activeVariationIndex.value],
        body: { blocks }
      }
    }

    // Update the original article with ALL changes
    const updatedArticle = {
      ...editingArticle.value,
      id: originalId // Preserve original ID
    }

    // Replace in documents array
    processedData.value.json.documents[editingArticleIndex.value] = updatedArticle

    console.log('Updated variation at index:', activeVariationIndex.value)
    console.log('Article updated with ID:', originalId)
    console.log('Current approval status AFTER save:', articleApprovalStates.value[editingArticleIndex.value])

    closeArticleEditor()
  }
}

const closeArticleEditor = () => {
  showArticleEditor.value = false
  editingArticle.value = null
  editingArticleIndex.value = -1
}

const updateLabels = () => {
  const labels = labelsText.value.split(',').map(label => label.trim()).filter(label => label.length > 0)
  editingArticle.value.draft.labels = labels.map(name => ({ name }))
}

const addAlternative = () => {
  if (!editingArticle.value.draft.alternatives) {
    editingArticle.value.draft.alternatives = []
  }
  editingArticle.value.draft.alternatives.push({ phrase: '', autocomplete: true })
}

const removeAlternative = (index) => {
  editingArticle.value.draft.alternatives.splice(index, 1)
}

const updateArticleStatus = () => {
  if (editingArticleStatus.value === 'Draft') {
    // Keep only draft, remove published
    if (!editingArticle.value.draft) {
      editingArticle.value.draft = editingArticle.value.published || {
        title: '',
        alternatives: [],
        category: { name: 'General Information' },
        labels: [],
        visible: true,
        variations: [{
          name: "Default",
          priority: 1,
          body: {
            blocks: [{
              type: "Paragraph",
              paragraph: {
                blocks: [{
                  type: "Text",
                  text: { text: "" }
                }]
              }
            }]
          }
        }]
      }
    }
    delete editingArticle.value.published
  } else {
    // Move draft to published
    editingArticle.value.published = editingArticle.value.draft
    delete editingArticle.value.draft
  }
}

const deleteArticle = (index) => {
  const article = processedData.value.json.documents[index]
  const articleTitle = getCurrentArticleData(article).title

  // Set up article to delete with necessary info
  articleToDelete.value = {
    index: index,
    title: articleTitle,
    genesys_document_id: article.genesys_document?.id,
    genesys_kb_id: processedData.value.import_result?.knowledgeBase?.id,
    isGeneratedArticle: true // Flag to indicate this is from generated articles list
  }

  showDeleteModal.value = true
}

// Knowledge base functions
const loadKnowledgeBases = async () => {
  loadingKnowledgeBases.value = true
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases?company=${company}`)
    const data = await response.json()

    if (data.success) {
      // Sort knowledge bases alphabetically by name
      knowledgeBases.value = data.knowledge_bases.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      )
      console.log(`Loaded ${data.knowledge_bases.length} knowledge bases`)
    } else {
      throw new Error('Failed to load knowledge bases')
    }
  } catch (error) {
    console.error('Failed to load knowledge bases:', error)
    knowledgeBases.value = []
  } finally {
    loadingKnowledgeBases.value = false
  }
}

const importApprovedArticles = async () => {
  if (!selectedKnowledgeBase.value) {
    alert('No knowledge base selected')
    return
  }

  if (!processedData.value?.json?.documents) {
    alert('No documents found to import')
    return
  }
  
  // Filter only approved articles
  const approvedArticlesList = processedData.value.json.documents.filter((_, index) => 
    articleApprovalStates.value[index] === 'approved'
  )

  if (approvedArticlesList.length === 0) {
    alert('No articles approved for import')
    return
  }

  importingManually.value = true

  try {
    const company = localStorage.getItem("companyID") || "default"

    // Delete old articles using selected KB, regardless of how processing was configured
    let sourceUrlForDelete = null

    // Helper to standardize source URL format
    const normalizeSourceUrl = (url) => {
      if (!url) return null
      // For files: convert "/uploaded-files/filename" → "Files: filename"
      if (url.includes('/uploaded-files/')) {
        const filename = url.split('/').pop()
        return `Files: ${filename}`
      }
      // For files already in "Files: ..." format
      if (url.startsWith('Files:')) {
        return url.split(',')[0].trim()  // Get first file
      }
      // For other sources (websites, etc)
      return url
    }

    // Only delete if user explicitly marked for replace (pendingReplaceInfo is set)
    const pendingReplaceRaw = localStorage.getItem('pendingReplaceInfo')
    if (pendingReplaceRaw) {
      try {
        const { sourceUrl } = JSON.parse(pendingReplaceRaw)
        sourceUrlForDelete = normalizeSourceUrl(sourceUrl)
      } catch (e) {
        console.error('Failed to parse pendingReplaceInfo:', e)
      }
      localStorage.removeItem('pendingReplaceInfo')
    }

    // If we have a source URL and KB, attempt to delete old articles
    if (sourceUrlForDelete && selectedKnowledgeBase.value?.id) {
      try {
        const kbIdForDelete = selectedKnowledgeBase.value.id
        const delResponse = await fetch(
          `${process.env.VUE_APP_AI_BASE_URL}/api/delete-genesys-articles?company=${company}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ source_url: sourceUrlForDelete, kb_id: kbIdForDelete })
          }
        )
        const delResult = await delResponse.json()
        console.log(`Deleted ${delResult.deleted_count} old article(s) from Genesys before replace import`)
      } catch (e) {
        console.error('Failed to delete old articles before replace import:', e)
        // Continue with import even if delete fails
      }
    }

    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/upload-to-knowledge-base?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        knowledge_base_id: selectedKnowledgeBase.value.id,
        json_data: {
          ...processedData.value.json,
          documents: approvedArticlesList
        }
      })
    })

    const result = await response.json()
    
    if (result.success) {
      console.log('Import completed successfully:', result)
      processedData.value.import_result = result
      importedApprovedCount.value = approvedArticlesList.length

      // Persist import result to localStorage so it survives page refresh
      localStorage.setItem('processedData', JSON.stringify(processedData.value))

      // Link articles with Genesys documents after successful import
      setTimeout(async () => {
        console.log('Linking articles with Genesys documents after import...')
        await linkArticlesWithGenesysDocuments()
      }, 2000) // Wait 2 seconds for Genesys to fully process the import
    } else {
      console.error('Import failed:', result.error)
      processedData.value.import_error = result.error
    }
  } catch (error) {
    console.error('Import request failed:', error)
    processedData.value.import_error = error.message
  } finally {
    importingManually.value = false
  }
}

// Historical articles functions
const loadHistoricalArticles = async () => {
  if (!processedData.value?.source_info) return

  loadingHistoricalArticles.value = true

  try {
    const company = localStorage.getItem("companyID") || "default"
    const sourceUrl = processedData.value.source_info

    console.log('Loading historical articles for:', sourceUrl)

    // Check if this is a file-based source (starts with "Files:")
    if (sourceUrl.startsWith('Files:')) {
      const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles?source_url=${encodeURIComponent(sourceUrl)}&company=${company}`)

      if (response.ok) {
        const data = await response.json()
        historicalArticles.value = data.articles || []
      } else {
        console.warn(`File-based articles not found for: ${sourceUrl}`)
        historicalArticles.value = []
      }
      return
    }

    // For URL-based sources
    let response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles?source_url=${encodeURIComponent(sourceUrl)}&company=${company}`)

    if (!response.ok && response.status === 404) {
      // Try site-based endpoint with filtering
      const urlObj = new URL(sourceUrl)
      const siteRootUrl = `${urlObj.protocol}//${urlObj.hostname}`

      response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles-by-site?company=${company}&site_url=${encodeURIComponent(siteRootUrl)}`)

      if (response.ok) {
        const allArticles = await response.json()
        historicalArticles.value = allArticles.filter(article =>
          article.source_url === sourceUrl || article.source_url === sourceUrl + '/' || article.source_url === sourceUrl.replace(/\/$/, '')
        )
      } else {
        historicalArticles.value = []
      }
    } else if (response.ok) {
      const data = await response.json()
      historicalArticles.value = data.articles || []
    } else {
      historicalArticles.value = []
    }
  } catch (error) {
    console.error('Error loading historical articles:', error)
    historicalArticles.value = []
  } finally {
    loadingHistoricalArticles.value = false
  }
}

watch(historicalArticles, () => {
  // Auto-open the most recent day panel and reset state
  if (historicalArticles.value.length > 0) {
    openDayPanels.value = [0]
  }
  visibleDayCount.value = 30
  dayPanelPages.value = {}
})

const historicalArticlesByDay = computed(() => {
  const groups = {}
  for (const article of historicalArticles.value) {
    const date = article.created_at ? new Date(article.created_at) : null
    const key = date
      ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : 'Unknown Date'
    if (!groups[key]) groups[key] = { label: key, date, articles: [] }
    groups[key].articles.push(article)
  }
  return Object.values(groups).sort((a, b) => {
    if (!a.date) return 1
    if (!b.date) return -1
    return b.date - a.date
  })
})

const visibleHistoricalDays = computed(() => historicalArticlesByDay.value.slice(0, visibleDayCount.value))
const hasMoreDays = computed(() => historicalArticlesByDay.value.length > visibleDayCount.value)
const remainingDayCount = computed(() => historicalArticlesByDay.value.length - visibleDayCount.value)

const loadMoreDays = () => { visibleDayCount.value += 30 }

const DAY_PAGE_SIZE = 25

const getDayPage = (label) => dayPanelPages.value[label] || 1

const setDayPage = (label, page) => { dayPanelPages.value = { ...dayPanelPages.value, [label]: page } }

const getDayTotalPages = (group) => Math.ceil(group.articles.length / DAY_PAGE_SIZE)

const getPaginatedArticles = (group) => {
  const page = getDayPage(group.label)
  const start = (page - 1) * DAY_PAGE_SIZE
  return group.articles.slice(start, start + DAY_PAGE_SIZE)
}

const formatHistoricalDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const getKnowledgeBaseName = (kbId) => {
  if (!kbId) return 'Unknown'
  const kb = knowledgeBases.value.find(kb => kb.id === kbId)
  return kb ? kb.name : kbId
}

const viewHistoricalArticle = (article) => {
  try {
    console.log('=== HISTORICAL ARTICLE PREVIEW ===')
    console.log('Article object:', article)
    console.log('Article content type:', typeof article.content)
    console.log('Article content:', article.content)

    // Check if content exists and is valid
    if (!article.content ||
        article.content === 'undefined' ||
        article.content === 'null' ||
        article.content.includes('"error": "Content missing')) {
      alert('Article content is missing. The article may need to be reprocessed.')
      return
    }

    // Parse the article content JSON
    let articleData
    if (typeof article.content === 'string') {
      articleData = JSON.parse(article.content)
    } else {
      articleData = article.content
    }

    console.log('Parsed articleData:', articleData)

    // Set up preview data
    previewingHistoricalArticle.value = {
      title: article.title,
      status: article.status,
      created_at: article.created_at,
      genesys_kb_id: article.genesys_kb_id,
      source_url: article.source_url || articleData?.draft?.sourceUrl,
      data: articleData,
      rawContent: article.content,
      genesys_document_id: article.genesys_document_id,
      genesys_document_state: article.genesys_document_state,
      genesys_document_uri: article.genesys_document_uri,
      import_job_id: article.import_job_id,
      import_upload_key: article.import_upload_key,
      import_date_created: article.import_date_created
    }

    console.log('Preview article set:', previewingHistoricalArticle.value)

    // Show preview modal
    showHistoricalArticleModal.value = true

  } catch (error) {
    console.error('Error parsing article content:', error)
    console.error('Content that failed to parse:', article.content)
    alert(`Failed to open article: ${error.message}`)
  }
}

const showDeleteConfirmation = (article) => {
  articleToDelete.value = article
  showDeleteModal.value = true
}

const cancelDelete = () => {
  articleToDelete.value = null
  showDeleteModal.value = false
}

const confirmDelete = async () => {
  if (!articleToDelete.value) return

  isDeleting.value = true

  try {
    const company = localStorage.getItem("companyID") || "default"

    // Handle generated articles (from current processing session)
    if (articleToDelete.value.isGeneratedArticle) {
      const index = articleToDelete.value.index

      // If article has been imported to Genesys, remove it from there first
      if (articleToDelete.value.genesys_document_id && articleToDelete.value.genesys_kb_id) {
        try {
          const response = await fetch(
            `${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases/${articleToDelete.value.genesys_kb_id}/documents/${articleToDelete.value.genesys_document_id}?company=${company}`,
            { method: 'DELETE' }
          )

          if (response.ok) {
            console.log('Removed article from Genesys KB')
          } else {
            console.warn('Failed to remove from Genesys, continuing with local deletion')
          }
        } catch (error) {
          console.warn('Failed to remove from Genesys, continuing with local deletion:', error)
        }
      }

      // Remove from local arrays
      processedData.value.json.documents.splice(index, 1)
      delete articleApprovalStates.value[index]

      const selectedIndex = selectedArticles.value.indexOf(index)
      if (selectedIndex > -1) {
        selectedArticles.value.splice(selectedIndex, 1)
      }

      showDeleteModal.value = false
      showToastMessage('success', 'Article deleted successfully')
      articleToDelete.value = null
    }
    // Handle historical articles (from database)
    else {
      const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/approved-articles/${articleToDelete.value.id}?company=${company}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        // Remove from local list
        historicalArticles.value = historicalArticles.value.filter(a => a.id !== articleToDelete.value.id)

        // Reload historical articles to refresh the list
        await loadHistoricalArticles()

        // Close modal and show success toast
        showDeleteModal.value = false
        showToastMessage('success', 'Article deleted successfully')
      } else {
        throw new Error('Failed to delete article')
      }

      articleToDelete.value = null
    }
  } catch (error) {
    console.error('Error deleting article:', error)
    showToastMessage('error', `Error deleting article: ${error.message}`)
    articleToDelete.value = null
  } finally {
    isDeleting.value = false
  }
}

const deleteHistoricalArticle = (article) => {
  showDeleteConfirmation(article)
}

const showToastMessage = (type, message) => {
  toastType.value = type
  toastMessage.value = message
  showToast.value = true

  // Auto-hide toast after 3 seconds
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

// Helper function to get nested fields from content
const getContentField = (data, path) => {
  if (!data) return null
  return path.split('.').reduce((obj, key) => obj?.[key], data?.draft || data)
}

// Helper function to render article content
const renderArticleContent = (data) => {
  if (!data) return '<p>No content available</p>'

  try {
    const draft = data.draft || data
    const variations = draft.variations || []

    if (!variations.length) return '<p>No content variations available</p>'

    const defaultVariation = variations.find(v => v.name === 'Default') || variations[0]
    const body = defaultVariation?.body

    if (!body?.blocks) return '<p>No content blocks available</p>'

    // Convert blocks to HTML
    let html = ''
    for (const block of body.blocks) {
      html += renderBlock(block)
    }

    return html || '<p>No renderable content</p>'
  } catch (error) {
    console.error('Error rendering content:', error)
    return '<p>Error rendering content</p>'
  }
}

// Helper function to render individual blocks
const renderBlock = (block) => {
  if (!block || !block.type) return ''

  switch (block.type) {
    case 'Paragraph':
      return renderParagraph(block.paragraph)
    case 'OrderedList':
      return renderOrderedList(block.list)
    case 'UnorderedList':
      return renderUnorderedList(block.list)
    case 'Image':
      return renderImage(block.image)
    case 'Video':
      return renderVideo(block.video)
    default:
      return ''
  }
}

const renderParagraph = (paragraph) => {
  if (!paragraph?.blocks) return ''
  let content = ''
  for (const block of paragraph.blocks) {
    if (block.type === 'Text') {
      content += block.text?.text || ''
    }
  }
  const fontType = paragraph.properties?.fontType || 'Paragraph'
  const tag = fontType.startsWith('Heading') ? fontType.toLowerCase().replace('heading', 'h') : 'p'
  return `<${tag}>${content}</${tag}>`
}

const renderListItemText = (block) => {
  if (block.type !== 'Text') return ''
  const raw = block.text?.text || ''
  const url = block.text?.hyperlink || ''
  if (url) return `<a href="${url}">${raw}</a>`
  return raw
}

const renderOrderedList = (list) => {
  if (!list?.blocks) return ''
  let html = '<ol>'
  for (const item of list.blocks) {
    if (item.type === 'ListItem') {
      const itemContent = (item.blocks || []).map(renderListItemText).join('')
      html += `<li>${itemContent}</li>`
    }
  }
  html += '</ol>'
  return html
}

const renderUnorderedList = (list) => {
  if (!list?.blocks) return ''
  let html = '<ul>'
  for (const item of list.blocks) {
    if (item.type === 'ListItem') {
      const itemContent = (item.blocks || []).map(renderListItemText).join('')
      html += `<li>${itemContent}</li>`
    }
  }
  html += '</ul>'
  return html
}

const renderImage = (image) => {
  const url = image?.url || ''
  const hyperlink = image?.hyperlink || ''
  if (hyperlink) {
    return `<a href="${hyperlink}" target="_blank"><img src="${url}" alt="Article Image" style="max-width: 100%;" /></a>`
  }
  return `<img src="${url}" alt="Article Image" style="max-width: 100%;" />`
}

const renderVideo = (video) => {
  const url = video?.url || ''
  return `<video controls style="max-width: 100%;"><source src="${url}" /></video>`
}

// Initialize with data from localStorage
onMounted(async () => {
  // Try to load from processedData key
  let storedData = localStorage.getItem('processedData')

  if (storedData) {
    try {
      const data = JSON.parse(storedData)
      processedData.value = data
      console.log('Loaded processedData from localStorage:', data)
    } catch (error) {
      console.error('Error parsing stored processedData:', error)
    }
  } else {
    // Fallback to legacy articleManagementData format
    console.log('processedData not found, trying articleManagementData...')
    const legacyData = localStorage.getItem('articleManagementData')
    if (legacyData) {
      try {
        const data = JSON.parse(legacyData)
        console.log('Found articleManagementData:', data)

        // Convert legacy format to processedData format
        processedData.value = {
          json: {
            documents: data.articles || []
          },
          source_info: data.sourceUrl || 'Legacy Data'
        }

        console.log('Converted legacy data to processedData format')
      } catch (error) {
        console.error('Error parsing stored articleManagementData:', error)
      }
    }
  }

  // Load selectedKnowledgeBase from articleManagementData
  const legacyData = localStorage.getItem('articleManagementData')
  if (legacyData) {
    try {
      const data = JSON.parse(legacyData)
      console.log('articleManagementData:', data)
      if (data.selectedKnowledgeBase) {
        selectedKnowledgeBase.value = data.selectedKnowledgeBase
        console.log('Loaded selectedKnowledgeBase:', selectedKnowledgeBase.value)
      } else {
        console.log('No selectedKnowledgeBase found in articleManagementData')
      }
    } catch (error) {
      console.error('Error loading selectedKnowledgeBase:', error)
    }
  } else {
    console.log('No articleManagementData found in localStorage')
  }

  // Load approval states
  const storedApprovalStates = localStorage.getItem('articleApprovalStates')
  if (storedApprovalStates) {
    try {
      articleApprovalStates.value = JSON.parse(storedApprovalStates)
      console.log('Loaded approval states:', articleApprovalStates.value)
    } catch (error) {
      console.error('Error parsing stored approval states:', error)
    }
  }

  // Auto-load knowledge bases
  console.log('Auto-loading knowledge bases on mount...')
  await loadKnowledgeBases()

  // Link articles with Genesys documents if we have import results
  if (processedData.value?.import_result) {
    console.log('Linking articles with Genesys documents...')
    await linkArticlesWithGenesysDocuments()
  }

  // Load historical articles if we have source info
  if (processedData.value?.source_info) {
    console.log('Loading historical articles...')
    await loadHistoricalArticles()
  }
})

// Watch for changes to source_info and automatically reload historical articles
watch(
  () => processedData.value?.source_info,
  async (newSourceInfo, oldSourceInfo) => {
    if (newSourceInfo && newSourceInfo !== oldSourceInfo) {
      console.log('Source info changed, reloading historical articles:', newSourceInfo)
      await loadHistoricalArticles()
    }
  },
  { immediate: false }
)
</script>

<style scoped>
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 8px 8px;
  font-size: 14px;
  color: #374151;
}
.pagination-info { font-weight: 500; }
.pagination-buttons { display: flex; align-items: center; gap: 4px; }
.pagination-btn {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
}
.pagination-btn:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
.pagination-btn:disabled { background: #f9fafb; border-color: #e5e7eb; color: #9ca3af; cursor: not-allowed; }
.page-numbers { display: flex; gap: 2px; margin: 0 8px; }
.page-number {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}
.page-number:hover { background: #f3f4f6; border-color: #9ca3af; }
.page-number.active { background: #3b82f6; border-color: #3b82f6; color: white; }
.per-page-selector { font-size: 13px; color: #6b7280; }
.per-page-selector select { margin-left: 6px; border: 1px solid #d1d5db; border-radius: 4px; padding: 3px 6px; font-size: 13px; }
/* Layout and body container */
.flex-child {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 88vh;
  padding: 0;
  background: #f8fafc;
  overflow-x: hidden !important;
}

.body-container {
  align-self: unset;
  height: auto;
  width: auto;
  min-height: calc(100vh - 16vh);
  display: flex;
  flex-direction: column;
}

/* Header styling */
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

header h2 {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.genesys-status {
  display: flex;
  align-items: center;
}

.status-indicator {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-indicator.connected {
  background: #dcfce7;
  color: #166534;
}

/* Subheader styling */
#subheader {
  display: flex;
  gap: 24px;
  padding: 12px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.info-label {
  font-weight: 600;
  color: #374151;
  font-size: 13px;
}

.info-value {
  color: #6b7280;
  font-size: 13px;
}

.info-value.approved-count {
  background: #dcfce7;
  color: #166534;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
}

.info-value.import-success {
  color: #059669;
  font-weight: 600;
}

/* Main content area */
#centerdiv {
  margin-top: 25px;
  display: flex;
  flex-wrap: nowrap;
  height: auto;
  width: 100%;
  justify-content: space-between;
  gap: 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 600px;
}

.articles-section-wrapper {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.articles-section {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 24px;
}

.articles-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f1f5f9;
}

.articles-header h4 {
  margin: 0;
  color: #111827;
  font-size: 18px;
  font-weight: 600;
}

.approval-summary-mini {
  display: flex;
  gap: 12px;
}

.mini-stat {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
}

.mini-stat.approved {
  background: #dcfce7;
  color: #166534;
}

.mini-stat.pending {
  background: #fef3c7;
  color: #92400e;
}

.mini-stat.rejected {
  background: #fecaca;
  color: #991b1b;
}

/* Selection Info Bar */
.selection-info-bar {
  padding: 16px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  margin-bottom: 20px;
}

.selection-info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.selection-info {
  color: #475569;
  font-size: 14px;
  font-weight: 500;
}

.bulk-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.bulk-action-btn {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.bulk-action-btn.approve-btn {
  background: #059669;
  color: white;
}

.bulk-action-btn.approve-btn:hover:not(:disabled) {
  background: #047857;
}

.bulk-action-btn.approve-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.bulk-action-btn.reject-btn {
  background: #dc2626;
  color: white;
}

.bulk-action-btn.reject-btn:hover:not(:disabled) {
  background: #b91c1c;
}

.bulk-action-btn.reject-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.bulk-action-btn.reset-btn {
  background: #6b7280;
  color: white;
}

.bulk-action-btn.reset-btn:hover:not(:disabled) {
  background: #4b5563;
}

.bulk-action-btn.reset-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.bulk-action-btn.import-btn {
  background: #3b82f6;
  color: white;
}

.bulk-action-btn.import-btn:hover:not(:disabled) {
  background: #2563eb;
}

.bulk-action-btn.import-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.import-status-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid;
}

.import-status-message.success {
  background: #dcfce7;
  color: #166534;
  border-color: #bbf7d0;
}

.import-status-message.success i {
  color: #16a34a;
}

.import-status-message.error {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}

.import-status-message.error i {
  color: #dc2626;
}

.kb-select-inline {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: white;
  color: #374151;
  cursor: pointer;
  min-width: 200px;
  transition: border-color 0.2s;
}

.kb-select-inline:hover {
  border-color: #3b82f6;
}

.kb-select-inline:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.kb-warning {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #d97706;
  font-size: 12px;
  font-weight: 500;
}

.kb-warning i {
  font-size: 14px;
}

/* Professional button styles */
.btn-rounded {
  border-radius: 20px;
}

/* Articles Table */
.articles-table-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 20px;
  width: 100%;
  max-width: 100%;
}

.articles-table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  background: white;
  table-layout: fixed;
}

.articles-table thead {
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
}

.articles-table th {
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  border-bottom: 2px solid #e5e7eb;
}

.articles-table td {
  padding: 16px 8px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.article-row:hover {
  background: #f8fafc;
}

.article-row.article-selected {
  background: #eff6ff;
}

.article-row.article-approved {
  border-left: 4px solid #10b981;
}

.article-row.article-rejected {
  border-left: 4px solid #ef4444;
}

.checkbox-column {
  width: 40px;
}

.checkbox-header-wrapper {
  display: flex;
  justify-content: center;
}

.checkbox-cell {
  text-align: center;
}

.select-all-checkbox, .article-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  justify-content: center;
}

.select-all-checkbox:disabled, .article-checkbox:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.title-column {
  width: 30%;
  min-width: 200px;
  max-width: 300px;
}

.article-title {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
  margin-bottom: 4px;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.article-preview {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
  opacity: 0.8;
}

.category-column {
  width: 10%;
  min-width: 100px;
  max-width: 120px;
}

.rectangle-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.category-badge {
  background: #1e40af;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
}

.status-column {
  width: 80px;
}

.status-badge.published {
  background: #dcfce7;
  color: #166534;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.visible-column {
  width: 80px;
}

.visibility-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
}

.visibility-checkbox {
  width: 12px;
  height: 12px;
  cursor: pointer;
}

.visibility-checkbox:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.labels-column {
  width: 15%;
  min-width: 120px;
  max-width: 150px;
}

.labels-list {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.label-tag {
  background: #dbeafe;
  color: #1e40af;
}

.actions-column {
  width: 12%;
  min-width: 100px;
  max-width: 120px;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.action-btn {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
  border: 1px solid #d1d5db;
  min-width: auto;
  height: 28px;
  white-space: nowrap;
}

.action-btn i {
  font-size: 11px;
}

.action-btn .btn-text {
  font-size: 11px;
  font-weight: 500;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn.edit-btn {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #2563eb;
}

.action-btn.edit-btn:hover:not(:disabled) {
  background: #dbeafe;
  border-color: #2563eb;
  color: #2563eb;
}

.action-btn.edit-btn:disabled {
  background: #e5e7eb;
  border-color: #9ca3af;
  color: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}

.action-btn.delete-btn {
  background: #fee2e2;
  border-color: #ef4444;
  color: #dc2626;
}

.action-btn.delete-btn:hover:not(:disabled) {
  background: #fecaca;
  border-color: #dc2626;
  color: #dc2626;
}

.action-btn.delete-btn:disabled {
  background: #e5e7eb;
  border-color: #9ca3af;
  color: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}


/* Historical Articles Section */
.historical-articles-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
}

.historical-articles-section .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.historical-articles-section h4 {
  margin: 0;
  color: #111827;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.historical-articles-section h4 i {
  color: #6b7280;
}

.historical-articles-subtitle {
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 16px;
  font-style: italic;
}

.refresh-btn {
  padding: 6px 12px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.loading-spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.historical-table-wrapper {
  overflow-x: auto;
  margin-top: 16px;
}

.historical-articles-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.historical-articles-table thead {
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
}

.historical-articles-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.historical-articles-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s;
}

.historical-articles-table tbody tr:hover {
  background: #f9fafb;
}

.historical-articles-table td {
  padding: 12px 16px;
  color: #374151;
}

.historical-articles-table .col-title {
  width: 30%;
  max-width: 280px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.historical-articles-table .col-date {
  width: 20%;
  color: #6b7280;
  font-size: 12px;
}

.historical-articles-table .col-kb {
  width: 20%;
}

.historical-articles-table .col-status {
  width: 12%;
}

.historical-articles-table .col-actions {
  width: 18%;
  text-align: right;
}

.article-title {
  font-weight: 500;
  color: #111827;
}

.kb-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #e0f2fe, #bae6fd);
  color: #0c4a6e;
  border: 1px solid #7dd3fc;
}

.article-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.action-btn-small {
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 500;
}

.action-btn-small.view-btn {
  color: #3b82f6;
  border-color: #3b82f6;
}

.action-btn-small.view-btn:hover {
  background: #eff6ff;
  border-color: #2563eb;
}

.action-btn-small.delete-btn {
  color: #ef4444;
  border-color: #ef4444;
}

.action-btn-small.delete-btn:hover {
  background: #fee2e2;
  border-color: #dc2626;
}

.historical-empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.historical-empty-state .empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
  color: #d1d5db;
}

.historical-empty-state p {
  margin: 0;
  font-size: 14px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  margin: 16px 24px 24px 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #d1d5db;
}

.empty-state h3 {
  font-size: 18px;
  color: #374151;
  margin-bottom: 8px;
}

.primary-btn {
  background: #1e40af;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  transition: background-color 0.2s;
}

.primary-btn:hover {
  background: #1d4ed8;
}

/* Editor Modal Styles */
.editor-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.editor-modal {
  background: white;
  border-radius: 8px;
  width: 90vw;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.editor-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 8px 8px 0 0;
}

.editor-modal-header h3 {
  margin: 0;
  color: #111827;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.editor-modal-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.editor-left-panel {
  width: 400px;
  flex-shrink: 0;
  padding: 24px;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  background: #fafbfc;
}

.editor-right-panel {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.edit-field {
  margin-bottom: 16px;
}

.edit-field label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #1e40af;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.current-labels-display {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  min-height: 40px;
  align-items: center;
}

.current-label-tag {
  background: #dbeafe;
  color: #1e40af;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.no-labels {
  color: #9ca3af;
  font-size: 13px;
  font-style: italic;
}

.alternatives-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alternative-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.alternative-input {
  flex: 1;
}

.btn-danger-small {
  background: #ef4444;
  color: white;
  border: none;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-danger-small:hover {
  background: #dc2626;
}

.content-editor {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  height: 100%;
  box-sizing: border-box;
}

.content-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.content-editor-header h4 {
  margin: 0;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.content-editor h4 {
  margin: 0 0 16px 0;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

/* ── Variation tabs bar ──────────────────────────────────────────── */
.variation-tabs-bar {
  display: flex;
  align-items: stretch;
  gap: 2px;
  padding: 0 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  /* bleed out of the right panel's 24px padding so the bar spans full width */
  margin: -24px -24px 16px -24px;
  /* push scrollbar below the tab border-bottom accent */
  padding-bottom: 12px;
  scrollbar-gutter: stable;
}

.variation-tab {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 16px 8px;
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -1px;
  transition: all 0.12s;
  white-space: nowrap;
}

.variation-tab:hover {
  background: #f3f4f6;
}

.variation-tab-active {
  border-bottom-color: #3b82f6;
  background: #fff;
}

.variation-tab-name {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.variation-tab-active .variation-tab-name {
  color: #1d4ed8;
}

.variation-tab-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.variation-tab-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
}

.tp-chip-blue   { background: #dbeafe; color: #1d4ed8; }
.tp-chip-purple { background: #ede9fe; color: #6d28d9; }
.tp-chip-teal   { background: #ccfbf1; color: #0f766e; }
.tp-chip-gray   { background: #f3f4f6; color: #6b7280; }

/* ── Touchpoint sub-tabs ─────────────────────────────────────────── */
.tp-subtabs-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 24px 0;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
  flex-wrap: wrap;
  flex-shrink: 0;
  /* bleed out to match the tabs bar */
  margin: -16px -24px 16px -24px;
}

.tp-subtab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px 5px;
  border-radius: 6px 6px 0 0;
  border: 1px solid #e5e7eb;
  border-bottom: none;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  background: #f9fafb;
  white-space: nowrap;
}

.tp-subtab-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── Content editor ──────────────────────────────────────────────── */
.content-editor-tabbed {
  border-top-left-radius: 0 !important;
  border-top: none !important;
}

.wysiwyg-editor-container {
  height: calc(100% - 60px);
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow-y: auto;
}

.wysiwyg-editor {
  height: 100%;
}

.editor-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-radius: 0 0 8px 8px;
}

.btn-primary {
  background: #1e40af;
  color: white;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #f8fafc;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

/* Responsive */
@media (max-width: 768px) {
  #centerdiv {
    margin: 16px 8px 20px 8px;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 16px;
    padding: 16px;
    width: auto;
  }
  
  .articles-section {
    padding: 16px;
  }
  
  .selection-info-bar {
    padding: 12px;
  }
  
  .empty-state {
    margin: 12px;
  }
  
  #subheader {
    padding: 12px;
    gap: 16px;
  }
  
  header {
    padding: 12px 16px;
  }
  
  .editor-modal {
    width: 95vw;
    height: 95vh;
  }
  
  .editor-modal-body {
    flex-direction: column;
  }
  
  .editor-left-panel {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    max-height: 300px;
  }
  
  .editor-right-panel {
    flex: 1;
  }
}

/* Header with Back Button */
.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f8fafc;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.back-button:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
  color: #1f2937;
}

.back-button i {
  font-size: 12px;
}

/* Article Preview Modal - Modern Design */
.article-preview-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.article-preview-modal {
  background: #ffffff;
  border-radius: 20px;
  max-width: 900px;
  max-height: 90vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

/* Header */
.article-preview-modal .modal-header {
  padding: 32px 32px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon {
  font-size: 28px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 12px;
  color: white;
  flex-shrink: 0;
}

.header-text h3 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.article-preview-modal .close {
  position: absolute;
  top: 24px;
  right: 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  font-size: 16px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.article-preview-modal .close:hover {
  background: #f1f5f9;
  color: #334155;
  transform: scale(1.05);
}

/* Content */
.article-preview-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.article-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.article-info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.article-info-item strong {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.article-info-item span {
  font-size: 15px;
  color: #1e293b;
  font-weight: 500;
  word-break: break-word;
}

.source-url, .kb-id, .genesys-document-id {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px !important;
  background: #ffffff;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* Meta Information */
.article-meta-info {
  margin-bottom: 32px;
}

.article-meta-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;
}

.article-meta-row strong {
  font-size: 14px;
  font-weight: 700;
  color: #475569;
  width: 120px;
  flex-shrink: 0;
  padding-top: 2px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-tag {
  background: linear-gradient(135deg, #e0f2fe, #bae6fd);
  color: #0c4a6e;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #7dd3fc;
}

.label-tag {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #93c5fd;
}

.alternative-item {
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  color: #166534;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #bbf7d0;
}

/* Content Section */
.content-preview-section {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  overflow: hidden;
}

.content-preview-section h4 {
  margin: 0;
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.content-text {
  padding: 24px;
  line-height: 1.7;
  color: #334155;
  font-size: 15px;
}

.content-text h3 {
  color: #1e293b;
  font-weight: 700;
  font-size: 18px;
  margin: 0 0 16px 0;
}

.content-text h4 {
  color: #334155;
  font-weight: 600;
  font-size: 16px;
  margin: 24px 0 12px 0;
}

.content-text p {
  margin: 0 0 16px 0;
}

.content-text p:last-child {
  margin-bottom: 0;
}

.content-text strong {
  color: #1e293b;
  font-weight: 600;
}

.content-text a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.content-text a:hover {
  text-decoration: underline;
}

.content-text ol, .content-text ul {
  margin: 16px 0;
  padding-left: 24px;
}

.content-text li {
  margin: 8px 0;
}

/* Footer */
.article-preview-modal .modal-footer {
  padding: 20px 32px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  display: flex;
  justify-content: flex-end;
}

.article-preview-modal .btn-secondary {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  color: #475569;
  border: 1px solid #cbd5e1;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.article-preview-modal .btn-secondary:hover {
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Delete Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.delete-confirmation-modal {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.modal-header-delete {
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #fecaca;
}

.warning-icon {
  font-size: 32px;
  line-height: 1;
}

.modal-header-delete h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #991b1b;
}

.modal-body-delete {
  padding: 24px;
}

.modal-body-delete p {
  margin: 0 0 12px 0;
  color: #374151;
  font-size: 15px;
  line-height: 1.6;
}

.article-title-delete {
  font-weight: 600;
  color: #111827;
  font-size: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid #dc2626;
  margin: 16px 0 !important;
}

.warning-text {
  color: #dc2626;
  font-size: 13px !important;
  font-weight: 500;
}

.modal-footer-delete {
  padding: 20px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel {
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.btn-cancel:disabled {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-delete {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-delete:hover {
  background: linear-gradient(135deg, #b91c1c, #991b1b);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-delete:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

/* Toast Notification */
.toast-notification {
  position: fixed;
  top: 24px;
  right: 24px;
  min-width: 320px;
  max-width: 500px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  z-index: 10001;
  border-left: 4px solid;
}

.toast-notification.success {
  border-left-color: #10b981;
}

.toast-notification.error {
  border-left-color: #ef4444;
}

.toast-notification.info {
  border-left-color: #3b82f6;
}

.toast-icon {
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-notification.success .toast-icon {
  color: #10b981;
}

.toast-notification.error .toast-icon {
  color: #ef4444;
}

.toast-notification.info .toast-icon {
  color: #3b82f6;
}

.toast-message {
  flex: 1;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.toast-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: color 0.2s;
}

.toast-close:hover {
  color: #6b7280;
}

/* Toast Animation */
.toast-enter-active {
  animation: toast-slide-in 0.3s ease-out;
}

.toast-leave-active {
  animation: toast-slide-out 0.3s ease-in;
}

@keyframes toast-slide-in {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes toast-slide-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(400px);
    opacity: 0;
  }
}

/* Historical Articles Accordion */
.historical-accordion-app {
  background: transparent;
}

.historical-accordion {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.day-panel-title {
  font-weight: 600 !important;
  background: #f8f9fa !important;
}

.day-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.day-icon {
  color: #6c757d;
  font-size: 14px;
}

.day-label {
  font-size: 15px;
  color: #2c3e50;
  font-weight: 600;
}

.day-count {
  margin-left: auto;
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 10px;
  border-radius: 12px;
}

.day-panel-content :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 !important;
  margin: 0 !important;
}

.day-panel-content {
  overflow-x: auto;
}

.day-panel-content .historical-articles-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.day-panel-content .historical-articles-table thead tr {
  background: #f1f3f5;
}

.day-panel-content .historical-articles-table th {
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.day-panel-content .historical-articles-table td {
  padding: 9px 12px;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: middle;
}

.day-panel-content .historical-articles-table tr:last-child td {
  border-bottom: none;
}

.day-panel-content .historical-articles-table tr:hover td {
  background: #f8f9fa;
}

.col-time {
  white-space: nowrap;
  color: #6c757d;
  font-size: 12px;
}

/* Per-panel pagination */
.day-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.day-pagination-info {
  font-size: 12px;
  color: #6c757d;
}

.day-pagination-buttons {
  display: flex;
  gap: 4px;
  align-items: center;
}

.day-page-btn {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
}

.day-page-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
}

.day-page-btn.active {
  background: #1565c0;
  border-color: #1565c0;
  color: #fff;
  font-weight: 600;
}

.day-page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Load more days */
.load-more-days {
  display: flex;
  justify-content: center;
  padding: 16px 0 4px;
}

.load-more-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
}

.load-more-btn:hover {
  background: #f1f3f5;
  border-color: #adb5bd;
  color: #1565c0;
}

.load-more-btn .remaining-count {
  font-size: 11px;
  color: #adb5bd;
  font-weight: 400;
}
</style>